import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, BehaviorSubject, pipe } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { TenantService } from '../services';
import { map, takeUntil, debounceTime, filter, flatMap, tap } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import * as _ from 'lodash';
import { ConfirmationDialogComponent } from '../general/confirmationDialog.component';

@Component({
    styles: [`
        .tagsPanel {
            overflow-y: auto;
            height: calc(100vh - 130px);
        }
    `],
    template: `
        <div fxLayout="column">
            <mat-toolbar>
                <h3>Tags</h3>
                <span fxFlex></span>
                <mat-form-field style="font-size: 16px; font-weight: 400;">
                    <input matInput placeholder="Search Term" [formControl]="searchControl">
                </mat-form-field>
            </mat-toolbar>
            <div *ngIf="noTouching" fxFlex fxLayoutAlign="center center"><spinner></spinner></div>
            <div class="tagsPanel">
                <table mat-table
                       matSort 
                       (matSortChange)="sort($event)"
                       [matSortDisableClear]="true "
                       [matSortActive]="params?.value?.sort" 
                       [matSortDirection]="params?.value?.sortDirection" 
                       *ngIf="!noTouching" 
                       [dataSource]="tags" 
                       style="width: 100%;">
                    <ng-container matColumnDef="label">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="labelLower" width="20%">Label</th>
                        <td mat-cell *matCellDef="let row">{{row.label}}</td>
                    </ng-container>
                    <ng-container matColumnDef="created">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="created" start="desc">Created</th>
                        <td mat-cell *matCellDef="let row">{{row.created | date:'shortDate'}}</td>
                    </ng-container>
                    <ng-container matColumnDef="streamCount">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="streamCount" start="desc" style="padding-left: 15px;">Streams</   th>
                        <td mat-cell *matCellDef="let row">{{row.streamCount}}</td>
                    </ng-container>
                    <ng-container matColumnDef="actions">
                        <th mat-header-cell *matHeaderCellDef  width="8%"></th>
                        <td mat-cell *matCellDef="let row">
                            <mat-menu #menu="matMenu">
                                <a mat-menu-item [routerLink]="[row.id]">
                                    <mat-icon>build</mat-icon>
                                    <span>Edit</span>
                                </a>
                                <button mat-menu-item (click)="delete(row)"><mat-icon>clear</mat-icon>Delete</button>
                            </mat-menu>
                            <button mat-icon-button class="edit-button" mat-icon-button [matMenuTriggerFor]="menu">
                                <mat-icon>settings</mat-icon>
                            </button>
                        </td>
                    </ng-container>
                    <tr mat-header-row *matHeaderRowDef="['label', 'created', 'streamCount', 'actions']"></tr>
                    <tr mat-row *matRowDef="let row; columns: ['label', 'created', 'streamCount', 'actions'];"></tr>
                </table>
            </div>
        </div>
    `
})
export class SharedTagListComponent implements OnInit, OnDestroy {
    private cleanupTime = new Subject<void>();
    searchControl = new FormControl();
    noTouching: boolean;
    allTags: any;
    params = new BehaviorSubject<any>({  term: '', sort: 'label', sortDirection: 'asc' });
    tags = new BehaviorSubject<any[]>([]);
    lastFetched: boolean;
       
    constructor(private snackBar: MatSnackBar, private dialog: MatDialog, private tenantService: TenantService) {}
    
    ngOnInit(): void {
        this.fetchTags().pipe(tap(() => this.sort({ active: 'titleLower', direction: 'asc' }))).subscribe();
        
        this.params
        .pipe(
            filter(() => !!this.allTags),
            map(params => {
                let filtered = this.allTags.filter(tag => !params.term || !params.term.trim().length || tag.label.toLowerCase().includes(params.term));
                filtered = _.sortBy(filtered, [params.sort, 'labelLower']);
                if (params.sortDirection !== 'asc') {
                    filtered.reverse();
                }
                return filtered;
            }),
            takeUntil(this.cleanupTime)
        )
        .subscribe(tags => this.tags.next(tags));

        this.searchControl.valueChanges
        .pipe(
            debounceTime(500),
            takeUntil(this.cleanupTime)
        )
        .subscribe(term => {
            this.tags.next([]);
            this.params.next(Object.assign({}, this.params.value, { term }));
        });
    }

    fetchTags() {
        return this.tenantService.getCatalogTags()
        .pipe(
            tap(tags => tags.forEach(tag => tag.labelLower = tag.label.toLowerCase())),
            tap(tags => this.allTags = tags),
            tap(() => this.params.next(this.params.value))
        );
    }

    sort(sort: Sort): void {
        const params = this.params.value;
        params.page = 0;
        params.sort = sort.active;
        params.sortDirection = sort.direction;
        this.tags.next([]);
        this.params.next(params);
    }

    delete(tag): void {
        this.dialog.open(ConfirmationDialogComponent, { 
        data: {
            title: `Delete ${tag.label}?`, 
            actionLabel: 'Delete'}
        }).afterClosed()
        .pipe(
            filter(doIt => doIt),
            flatMap(() => this.tenantService.deleteCatalogTag(tag)),
            tap(() => this.snackBar.open(`${tag.label} has been deleted.`, 'OK', { duration: 3000})),
            flatMap(() => this.fetchTags())
        ).subscribe();
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}